import { useState, useEffect } from "react";
import axios from "axios";
import {
  TextField,
  Button,
  Typography,
  Alert,
  Box,
  Grid,
  Paper,
  Divider,
  Chip,
  InputAdornment,
  Tooltip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Avatar,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SaveIcon from "@mui/icons-material/Save";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import VideocamIcon from "@mui/icons-material/Videocam";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CircularProgress from "@mui/material/CircularProgress";
import Fade from "@mui/material/Fade";
import Backdrop from "@mui/material/Backdrop";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import WorkIcon from "@mui/icons-material/Work";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:3001";

// 修改后的玻璃卡片（无hover效果）
const GlassCard = styled(Paper)(({ theme }) => ({
  background: "rgba(0, 0, 0, 0.25)",
  backdropFilter: "blur(15px)",
  WebkitBackdropFilter: "blur(15px)",
  borderRadius: "24px",
  padding: theme.spacing(4),
  border: "1px solid rgba(255, 255, 255, 0.2)",
  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.2)",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  position: "relative",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "rgba(255, 255, 255, 0.05)",
    borderRadius: 24,
    pointerEvents: "none",
  },
  // 移除 hover 效果
}));

// 毛玻璃效果的输入框，helperText白色
const GlassTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    background: "rgba(255, 255, 255, 0.1)",
    borderRadius: "12px",
    "& fieldset": {
      borderColor: "rgba(255, 255, 255, 0.2)",
    },
    "&:hover fieldset": {
      borderColor: "rgba(255, 255, 255, 0.3)",
    },
    "&.Mui-focused fieldset": {
      borderColor: "rgba(255, 255, 255, 0.4)",
    },
  },
  "& .MuiInputLabel-root": {
    color: "#fff",
    fontSize: "0.9rem",
    "&.Mui-focused": {
      color: "#fff",
    },
  },
  "& .MuiInputBase-input": {
    color: "#fff",
    fontSize: "0.95rem",
  },
  "& .MuiFormHelperText-root": {
    color: "#fff !important",
    opacity: 1,
  },
  marginBottom: theme.spacing(3),
}));

// 按钮白色（禁用和普通），移除hover效果，全部文字白色
const GlassButton = styled(Button)(({ theme, color = "#2196f3" }) => ({
  background: `linear-gradient(135deg, ${color}15, ${color}05)`,
  backdropFilter: "blur(6px)",
  WebkitBackdropFilter: "blur(6px)",
  borderRadius: "8px",
  color: "#fff",
  padding: theme.spacing(1, 3),
  border: `1px solid ${color}20`,
  transition: "all 0.3s ease",
  "&:hover": {
    background: `linear-gradient(135deg, ${color}15, ${color}05)`,
    color: "#fff",
    boxShadow: "none",
    transform: "none",
  },
  "&:disabled": {
    background: "rgba(255, 255, 255, 0.1)",
    color: "#fff",
    opacity: 0.7,
  },
}));

// 表格的全部文字白色，透明背景，无hover效果
const GlassTable = styled(Table)(({ theme }) => ({
  "& .MuiTableCell-root": {
    borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
    background: "transparent",
    color: "#fff",
    textShadow: "0 1px 2px rgba(0,0,0,0.2)",
    position: "relative",
    zIndex: 1,
    fontSize: "0.95rem",
    padding: theme.spacing(2),
  },
  "& .MuiTableHead-root .MuiTableCell-root": {
    background: "transparent",
    fontWeight: 600,
    fontSize: "1rem",
    color: "#fff",
  },
  // 移除 hover 效果
}));

// 对话框
const GlassDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    background: "rgba(50, 50, 50, 0.96)",
    backdropFilter: "blur(15px)",
    WebkitBackdropFilter: "blur(15px)",
    borderRadius: "16px",
    border: "1px solid rgba(255, 255, 255, 0.2)",
    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
    color: "#fff",
  },
}));

// 加载遮罩
const LoadingBackdrop = styled(Backdrop)(({ theme }) => ({
  zIndex: theme.zIndex.drawer + 1,
  backgroundColor: "rgba(255, 255, 255, 0.7)",
  backdropFilter: "blur(4px)",
}));

// 分组标题
const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: "1rem",
  fontWeight: 600,
  color: "#fff",
  textShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
  position: "relative",
  zIndex: 1,
  marginBottom: theme.spacing(2),
  marginTop: theme.spacing(2),
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
}));

// 标题容器
const TitleContainer = styled(Box)(({ theme }) => ({
  height: "40px",
  display: "flex",
  alignItems: "center",
}));

// 头像上传按钮，全部白色，无hover增亮
const UploadButton = styled("label")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  padding: theme.spacing(1, 2),
  borderRadius: "12px",
  backgroundColor: "rgba(255, 255, 255, 0.1)",
  backdropFilter: "blur(6px)",
  WebkitBackdropFilter: "blur(6px)",
  border: "1px solid rgba(255, 255, 255, 0.2)",
  cursor: "pointer",
  transition: "all 0.3s ease",
  color: "#fff",
  "&:hover": {
    backgroundColor: "rgba(255, 255, 255, 0.1)",
    color: "#fff",
    transform: "none",
  },
}));

// 头像预览样式
const AvatarPreview = styled("img")(({ theme }) => ({
  width: "100px",
  height: "100px",
  objectFit: "cover",
  borderRadius: "12px",
  marginBottom: theme.spacing(2),
  border: "1px solid rgba(255, 255, 255, 0.2)",
  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
}));

const MinistryWorkerManagement = () => {
  const [ministryWorkers, setMinistryWorkers] = useState([]);
  const [name, setName] = useState("");
  const [introduction, setIntroduction] = useState("");
  const [videoUrl, setVideoUrl] = useState("");
  const [position, setPosition] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [currentId, setCurrentId] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [open, setOpen] = useState(false);
  const [editingWorker, setEditingWorker] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    introduction: "",
    videoUrl: "",
    position: "",
    email: "",
    phone: "",
    avatar: "",
  });
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState("");

  // 获取所有事奉同工
  const fetchMinistryWorkers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/ministry-worker`);
      setMinistryWorkers(response.data);
    } catch (err) {
      setError(err.response?.data?.message || "获取数据失败");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMinistryWorkers();
  }, []);

  // 重置表单
  const resetForm = () => {
    setName("");
    setIntroduction("");
    setVideoUrl("");
    setPosition("");
    setEmail("");
    setPhone("");
    setCurrentId(null);
    setIsEditing(false);
    setAvatarFile(null);
    setAvatarPreview("");
  };

  // 获取图片完整URL
  const getImageUrl = (path) => {
    if (!path) return `${API_BASE_URL}/images/avatars/default-avatar.png`;
    return `${API_BASE_URL}${path}`;
  };

  // 处理头像上传
  const handleAvatarChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setAvatarFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatarPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // 处理表单提交
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      let avatarUrl = formData.avatar;
      if (avatarFile) {
        const uploadFormData = new FormData();
        uploadFormData.append("avatar", avatarFile);
        const uploadResponse = await axios.post(
          `${API_BASE_URL}/upload/avatar`,
          uploadFormData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );
        avatarUrl = uploadResponse.data.url;
      }

      const data = {
        name,
        introduction,
        videoUrl,
        position: position.trim() || null,
        email: email.trim() || null,
        phone: phone.trim() || null,
        isActive: true,
        avatar: avatarUrl || "/images/avatars/default-avatar.png",
      };

      let response;
      if (currentId) {
        response = await axios.put(
          `${API_BASE_URL}/ministry-worker/${currentId}`,
          data
        );
      } else {
        response = await axios.post(`${API_BASE_URL}/ministry-worker`, data);
      }

      setSuccess(currentId ? "更新成功" : "创建成功");
      setCurrentId(null);
      setIsEditing(false);
      resetForm();
      await fetchMinistryWorkers();
    } catch (error) {
      setError(
        error.response?.data?.message || (currentId ? "更新失败" : "创建失败")
      );
    } finally {
      setLoading(false);
    }
  };

  // 处理编辑
  const handleEdit = async (id) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/ministry-worker/${id}`);
      const ministryWorker = response.data;

      setName(ministryWorker.name);
      setIntroduction(ministryWorker.introduction);
      setVideoUrl(ministryWorker.videoUrl || "");
      setPosition(ministryWorker.position);
      setEmail(ministryWorker.email || "");
      setPhone(ministryWorker.phone || "");
      setCurrentId(id);
      setIsEditing(true);
      setFormData({
        name: ministryWorker.name,
        introduction: ministryWorker.introduction,
        videoUrl: ministryWorker.videoUrl || "",
        position: ministryWorker.position,
        email: ministryWorker.email || "",
        phone: ministryWorker.phone || "",
        avatar: ministryWorker.avatar || "",
      });
      setAvatarPreview(ministryWorker.avatar || "");
    } catch (err) {
      setError(err.response?.data?.message || "获取同工详情失败");
    } finally {
      setLoading(false);
    }
  };

  // 处理删除
  const handleOpenDeleteDialog = (id) => {
    setDeleteId(id);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteId(null);
    setOpenDeleteDialog(false);
  };

  const handleDelete = async () => {
    try {
      setLoading(true);
      await axios.delete(`${API_BASE_URL}/ministry-worker/${deleteId}`);
      setSuccess("事奉同工删除成功");
      // 重新获取同工列表
      await fetchMinistryWorkers();
    } catch (err) {
      setError(err.response?.data?.message || "删除失败");
    } finally {
      setLoading(false);
      handleCloseDeleteDialog();
    }
  };

  // 所有icon使用统一的白色
  const iconCommonSx = { color: "#fff" };

  return (
    <Box sx={{ p: 3 }}>
      <Typography
        variant="h4"
        sx={{
          mb: 4,
          fontWeight: 500,
          color: "#fff",
          textShadow: "0 1px 2px rgba(0,0,0,0.2)",
          position: "relative",
          zIndex: 1,
        }}
      >
        同工管理
      </Typography>

      {success && (
        <Alert
          severity="success"
          sx={{
            mb: 3,
            backgroundColor: "rgba(237, 247, 237, 0.6)",
            border: "1px solid rgba(46, 125, 50, 0.1)",
            borderRadius: "8px",
          }}
          onClose={() => setSuccess("")}
        >
          {success}
        </Alert>
      )}

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 3, color: "#fff", background: "rgba(211,47,47,0.3)" }}
          onClose={() => setError("")}
        >
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* 表单部分 */}
        <Grid item xs={12} md={4}>
          <GlassCard>
            <Box sx={{ position: "relative", zIndex: 1 }}>
              <Typography
                variant="h6"
                sx={{
                  mb: 3,
                  fontWeight: 500,
                  color: "#fff",
                  textShadow: "0 1px 2px rgba(0,0,0,0.2)",
                }}
              >
                {isEditing ? "编辑同工" : "添加新同工"}
              </Typography>

              <form onSubmit={handleSubmit}>
                <Box sx={{ mb: 3 }}>
                  {avatarPreview && (
                    <Box sx={{ mb: 2 }}>
                      <AvatarPreview src={avatarPreview} alt="Avatar preview" />
                    </Box>
                  )}
                  <UploadButton>
                    <input
                      type="file"
                      accept="image/*"
                      hidden
                      onChange={handleAvatarChange}
                    />
                    <CloudUploadIcon sx={iconCommonSx} />
                    <Typography sx={{ color: "#fff" }}>上传头像</Typography>
                  </UploadButton>
                </Box>

                <GlassTextField
                  fullWidth
                  label="姓名"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon sx={iconCommonSx} />
                      </InputAdornment>
                    ),
                  }}
                />

                <GlassTextField
                  fullWidth
                  label="职位"
                  value={position}
                  onChange={(e) => setPosition(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <WorkIcon sx={iconCommonSx} />
                      </InputAdornment>
                    ),
                  }}
                />

                <GlassTextField
                  fullWidth
                  label="介绍"
                  value={introduction}
                  onChange={(e) => setIntroduction(e.target.value)}
                  multiline
                  rows={4}
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <InfoOutlinedIcon sx={iconCommonSx} />
                      </InputAdornment>
                    ),
                  }}
                />

                <GlassTextField
                  fullWidth
                  label="邮箱"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  type="email"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon sx={iconCommonSx} />
                      </InputAdornment>
                    ),
                  }}
                />

                <GlassTextField
                  fullWidth
                  label="电话"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PhoneIcon sx={iconCommonSx} />
                      </InputAdornment>
                    ),
                  }}
                />

                <GlassTextField
                  fullWidth
                  label="视频URL"
                  value={videoUrl}
                  onChange={(e) => setVideoUrl(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <VideocamIcon sx={iconCommonSx} />
                      </InputAdornment>
                    ),
                  }}
                />

                <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
                  <GlassButton
                    type="submit"
                    variant="contained"
                    startIcon={<SaveIcon sx={iconCommonSx} />}
                    disabled={loading}
                    color="#2196f3"
                  >
                    {isEditing ? "更新" : "添加"}
                  </GlassButton>

                  {isEditing && (
                    <GlassButton
                      variant="outlined"
                      onClick={resetForm}
                      color="#4caf50"
                    >
                      取消编辑
                    </GlassButton>
                  )}
                </Box>
              </form>
            </Box>
          </GlassCard>
        </Grid>

        {/* 列表部分 */}
        <Grid item xs={12} md={8}>
          <GlassCard>
            <Box sx={{ position: "relative", zIndex: 1 }}>
              <Typography
                variant="h6"
                sx={{
                  mb: 3,
                  fontWeight: 500,
                  color: "#fff",
                  textShadow: "0 1px 2px rgba(0,0,0,0.2)",
                }}
              >
                同工列表
              </Typography>

              {ministryWorkers.length === 0 ? (
                <Box sx={{ textAlign: "center", py: 4 }}>
                  <Typography
                    variant="body1"
                    sx={{
                      color: "#fff",
                      textShadow: "0 1px 2px rgba(0,0,0,0.2)",
                      position: "relative",
                      zIndex: 1,
                    }}
                  >
                    暂无同工数据
                  </Typography>
                </Box>
              ) : (
                <TableContainer>
                  <GlassTable>
                    <TableHead>
                      <TableRow>
                        <TableCell>头像</TableCell>
                        <TableCell>姓名</TableCell>
                        <TableCell>职位</TableCell>
                        <TableCell>联系方式</TableCell>
                        <TableCell>操作</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {ministryWorkers.map((worker) => (
                        <TableRow key={worker._id}>
                          <TableCell>
                            <Avatar
                              src={getImageUrl(worker.avatar)}
                              sx={{
                                color: "#fff",
                                background: "rgba(255,255,255,0.1)",
                              }}
                            >
                              {worker.name.charAt(0)}
                            </Avatar>
                          </TableCell>
                          <TableCell>{worker.name}</TableCell>
                          <TableCell>{worker.position}</TableCell>
                          <TableCell>
                            {worker.email && (
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                  color: "#fff",
                                }}
                              >
                                <EmailIcon
                                  fontSize="small"
                                  sx={{
                                    color: "#fff",
                                    textShadow: "0 1px 2px rgba(0,0,0,0.2)",
                                    position: "relative",
                                    zIndex: 1,
                                  }}
                                />
                                <Typography
                                  variant="body2"
                                  sx={{ color: "#fff" }}
                                >
                                  {worker.email}
                                </Typography>
                              </Box>
                            )}
                            {worker.phone && (
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                  mt: 0.5,
                                  color: "#fff",
                                }}
                              >
                                <PhoneIcon
                                  fontSize="small"
                                  sx={{
                                    color: "#fff",
                                    textShadow: "0 1px 2px rgba(0,0,0,0.2)",
                                    position: "relative",
                                    zIndex: 1,
                                  }}
                                />
                                <Typography
                                  variant="body2"
                                  sx={{ color: "#fff" }}
                                >
                                  {worker.phone}
                                </Typography>
                              </Box>
                            )}
                          </TableCell>
                          <TableCell>
                            <IconButton
                              size="small"
                              onClick={() => handleEdit(worker._id)}
                              sx={{ color: "#fff" }}
                            >
                              <EditIcon
                                fontSize="small"
                                sx={{ color: "#fff" }}
                              />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleOpenDeleteDialog(worker._id)}
                              sx={{ color: "#fff" }}
                            >
                              <DeleteIcon
                                fontSize="small"
                                sx={{ color: "#fff" }}
                              />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </GlassTable>
                </TableContainer>
              )}
            </Box>
          </GlassCard>
        </Grid>
      </Grid>

      {/* 删除确认对话框 */}
      <GlassDialog open={openDeleteDialog} onClose={handleCloseDeleteDialog}>
        <DialogTitle sx={{ color: "#fff" }}>确认删除</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: "#fff" }}>
            您确定要删除这个同工记录吗？此操作无法撤销。
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} sx={{ color: "#fff" }}>
            取消
          </Button>
          <Button
            onClick={handleDelete}
            color="error"
            autoFocus
            sx={{ color: "#fff" }}
          >
            删除
          </Button>
        </DialogActions>
      </GlassDialog>

      {/* 加载中遮罩 */}
      <LoadingBackdrop open={loading}>
        <CircularProgress color="inherit" />
      </LoadingBackdrop>
    </Box>
  );
};

export default MinistryWorkerManagement;
